import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Clock, AlertCircle, Download, Printer } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime } from '../lib/utils'

interface ShiftRow {
  id: string
  store_id: string
  status: 'open' | 'closed'
  opened_at: string
  closed_at: string | null
  starting_float_fils: number
  expected_cash_fils: number | null
  actual_cash_fils: number | null
  variance_fils: number | null
  variance_reason: string | null
  opened_by_cashier_name: string | null
  closed_by_cashier_name: string | null
  variance_authorised_by_admin_email: string | null
}

interface ZReport {
  shift: ShiftRow
  sales_by_method: { payment_method: string; cnt: number; gross_fils: number; discount_fils: number }[]
  sales_total_count: number
  sales_total_gross_fils: number
  voids: { cnt: number; total_fils: number }
  returns_by_method: { refund_method: string; cnt: number; total_fils: number }[]
  returns_total_count: number
  returns_total_fils: number
  ledgers: { drops_earned: number; loyalty_points_earned: number; store_credit_applied_fils: number }
  top_by_units: { name: string | null; units: number; revenue_fils: number }[]
  top_by_revenue: { name: string | null; units: number; revenue_fils: number }[]
  expected_cash_fils: number
  cash_sales_fils: number
  cash_returns_fils: number
}

type StatusFilter = 'all' | 'open' | 'closed'
type VarianceFilter = 'all' | 'zero' | 'nonzero' | 'over_threshold'

function ShiftDetailPanel({ shiftId, onClose }: { shiftId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery<ZReport>({
    queryKey: ['shift', shiftId],
    queryFn: () => api.get(`/shifts/${shiftId}`).then(r => r.data),
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end print:bg-white print:relative print:inset-auto" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[#0f0f0f] text-gray-200 overflow-y-auto print:bg-white print:text-black print:max-w-full print:overflow-visible"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 px-4 py-3 border-b border-[#2e2e2e] flex items-center justify-between gap-2 bg-[#0f0f0f] print:hidden">
          <div>
            <h2 className="text-base font-semibold text-white">Z-report</h2>
            {data && (
              <p className="text-xs text-gray-500 mt-0.5">
                {formatDateTime(data.shift.opened_at)} → {data.shift.closed_at ? formatDateTime(data.shift.closed_at) : 'open'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-[#2a2a2a] text-gray-200 hover:bg-[#3a3a3a]"
                title="Print or save as PDF"
              >
                <Printer size={13} /> Print Z-report
              </button>
            )}
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
          </div>
        </div>
        {isLoading && <div className="p-4 text-sm text-gray-500">Loading…</div>}
        {error && <div className="p-4 text-sm text-red-400">Failed to load shift.</div>}
        {data && (
          <div className="p-4 space-y-4 text-sm zreport-printable">
            {/* Print-only header — hidden on screen, shown when printing so the
                paper has identifying context without the dark UI chrome. */}
            <div className="hidden print:block border-b border-black/30 pb-3 mb-3">
              <h2 className="text-lg font-bold">DrinX POS · Z-report</h2>
              <p className="text-xs text-gray-700 mt-1">
                Shift opened {formatDateTime(data.shift.opened_at)}{data.shift.closed_at ? ` · closed ${formatDateTime(data.shift.closed_at)}` : ' · still open'}
              </p>
            </div>

            <section className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 print:bg-transparent print:px-0 print:py-0">
              <Row k="Status" v={data.shift.status === 'open' ? 'Open' : 'Closed'} tone={data.shift.status === 'open' ? 'text-green-400 print:text-black' : ''} />
              <Row k="Opened by" v={data.shift.opened_by_cashier_name ?? '—'} />
              {data.shift.closed_by_cashier_name && <Row k="Closed by" v={data.shift.closed_by_cashier_name} />}
              <Row k="Starting float" v={formatCurrency(data.shift.starting_float_fils)} />
            </section>

            <section className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 print:bg-transparent print:px-0 print:py-0 print:border-t print:border-black/30 print:pt-3">
              <Row k={`Sales (${data.sales_total_count})`} v={formatCurrency(data.sales_total_gross_fils)} bold />
              {data.sales_by_method.map((m) => (
                <Row key={m.payment_method} k={`   ${methodLabel(m.payment_method)} (${m.cnt})`} v={formatCurrency(m.gross_fils)} dim />
              ))}
              {data.returns_total_count > 0 && (
                <>
                  <Row k={`Returns (${data.returns_total_count})`} v={`−${formatCurrency(data.returns_total_fils)}`} />
                  {data.returns_by_method.map((m) => (
                    <Row key={m.refund_method} k={`   ${refundMethodLabel(m.refund_method)} (${m.cnt})`} v={`−${formatCurrency(m.total_fils)}`} dim />
                  ))}
                </>
              )}
              {data.voids.cnt > 0 && (
                <Row k={`Voids (${data.voids.cnt})`} v={`−${formatCurrency(data.voids.total_fils)}`} tone="text-red-300 print:text-black" />
              )}
            </section>

            <section className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 print:bg-transparent print:px-0 print:py-0 print:border-t print:border-black/30 print:pt-3">
              <Row k="Cash sales" v={formatCurrency(data.cash_sales_fils)} />
              <Row k="Cash refunds" v={`−${formatCurrency(data.cash_returns_fils)}`} />
              <Row k="Expected drawer cash" v={formatCurrency(data.expected_cash_fils)} bold />
              {data.shift.actual_cash_fils !== null && (
                <>
                  <Row k="Counted cash" v={formatCurrency(data.shift.actual_cash_fils)} bold />
                  <Row
                    k="Variance"
                    v={`${(data.shift.variance_fils ?? 0) >= 0 ? '+' : ''}${formatCurrency(data.shift.variance_fils ?? 0)}`}
                    tone={(data.shift.variance_fils ?? 0) === 0 ? 'text-green-400 print:text-black' : 'text-red-300 print:text-black'}
                    bold
                  />
                  {data.shift.variance_reason && (
                    <p className="text-xs text-gray-500 italic pt-1 print:text-black">Reason: {data.shift.variance_reason}</p>
                  )}
                  {data.shift.variance_authorised_by_admin_email && (
                    <p className="text-xs text-gray-500 print:text-black">Authorised by {data.shift.variance_authorised_by_admin_email}</p>
                  )}
                </>
              )}
            </section>

            {(data.ledgers.drops_earned > 0 || data.ledgers.loyalty_points_earned > 0 || data.ledgers.store_credit_applied_fils > 0) && (
              <section className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 print:bg-transparent print:px-0 print:py-0 print:border-t print:border-black/30 print:pt-3">
                {data.ledgers.drops_earned > 0 && <Row k="Drops earned" v={`+${data.ledgers.drops_earned}`} />}
                {data.ledgers.loyalty_points_earned > 0 && <Row k="Loyalty points earned" v={`+${data.ledgers.loyalty_points_earned}`} />}
                {data.ledgers.store_credit_applied_fils > 0 && <Row k="Store credit applied" v={`−${formatCurrency(data.ledgers.store_credit_applied_fils)}`} />}
              </section>
            )}

            {data.top_by_units.length > 0 && (
              <section className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 print:bg-transparent print:px-0 print:py-0 print:border-t print:border-black/30 print:pt-3">
                <p className="text-xs uppercase tracking-wider text-gray-500 print:text-black">Top sellers</p>
                {data.top_by_units.map((t, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="truncate">{t.name ?? '(item)'}</span>
                    <span className="text-gray-500 shrink-0 print:text-black">{t.units} units · {formatCurrency(t.revenue_fils)}</span>
                  </div>
                ))}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ k, v, bold = false, dim = false, tone }: { k: string; v: string; bold?: boolean; dim?: boolean; tone?: string }) {
  return (
    <div className={`flex justify-between ${dim ? 'text-xs text-gray-500 print:text-black' : 'text-gray-300 print:text-black'} ${bold ? 'font-semibold text-white print:text-black' : ''} ${tone ?? ''}`}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  )
}

function methodLabel(m: string): string {
  switch (m) {
    case 'cod': case 'cash': return 'Cash'
    case 'card': return 'Card'
    case 'cliq': return 'CliQ'
    case 'store_credit': return 'Store credit'
    default: return m
  }
}

function refundMethodLabel(m: string): string {
  switch (m) {
    case 'cash': return 'Cash from drawer'
    case 'store_credit': return 'Store credit'
    case 'card': return 'Refund to card'
    default: return m
  }
}

export default function Shifts() {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [varianceFilter, setVarianceFilter] = useState<VarianceFilter>('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Build the query string used by both the JSON list and the CSV export so
  // the export downloads exactly what the operator sees on screen. Dates
  // are converted to ISO UTC timestamps client-side — `new Date('YYYY-MM-DD'...)`
  // interprets the string as LOCAL time, then .toISOString() shifts to UTC,
  // so picking "Jun 9" matches every shift that opened on Jun 9 in the
  // operator's timezone (not UTC). Worker compares via SQLite datetime()
  // so the stored UTC and the incoming ISO are normalised before comparison.
  const filterQuery = useMemo(() => {
    const p = new URLSearchParams()
    if (statusFilter !== 'all') p.set('status', statusFilter)
    if (varianceFilter !== 'all') p.set('variance', varianceFilter)
    if (fromDate) {
      p.set('from_iso', new Date(`${fromDate}T00:00:00`).toISOString())
    }
    if (toDate) {
      // 23:59:59.999 local → end-of-day-inclusive
      p.set('to_iso', new Date(`${toDate}T23:59:59.999`).toISOString())
    }
    return p.toString()
  }, [statusFilter, varianceFilter, fromDate, toDate])

  const { data, isLoading } = useQuery<{ shifts: ShiftRow[] }>({
    queryKey: ['shifts', filterQuery],
    queryFn: () => api.get(`/shifts${filterQuery ? `?${filterQuery}` : ''}`).then(r => r.data),
  })
  const shifts = data?.shifts ?? []

  const onExport = async () => {
    const res = await api.get(`/shifts/csv/export${filterQuery ? `?${filterQuery}` : ''}`, {
      responseType: 'blob',
    })
    const blob = res.data as Blob
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `shifts-export-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 space-y-4 print:p-0">
      {/* Print stylesheet — classic "print only this section" trick.
          visibility: hidden preserves the layout (so the absolute-
          positioned printable section knows where it sits) while making
          everything else invisible. Then the .zreport-printable subtree
          gets reset to visible, positioned at the top of the page, and
          forced to white/black for ink-friendly output. */}
      <style>{`
        @media print {
          html, body { background: white !important; }
          body * { visibility: hidden !important; }
          .zreport-printable, .zreport-printable * {
            visibility: visible !important;
            color: black !important;
            background: transparent !important;
            border-color: rgba(0,0,0,0.25) !important;
            box-shadow: none !important;
          }
          .zreport-printable {
            /* position: fixed in print mode pins to the print page itself
               (not the drawer's fixed-positioned parent), so the report
               spans the full page width instead of being constrained to
               the drawer's max-w-md. */
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 1.5cm !important;
            background: white !important;
            font-size: 11pt !important;
          }
          /* Print-only "DrinX POS — Z-report" header lives inside the
             printable subtree and uses tailwind print:block to appear. */
          .print\\:block { display: block !important; }
        }
      `}</style>

      <div className="flex items-center justify-between gap-2 print:hidden">
        <div className="flex items-center gap-2">
          <Clock size={20} className="text-[#C41E3A]" />
          <h1 className="text-2xl font-semibold text-white">Shifts (POS)</h1>
        </div>
        <button
          onClick={onExport}
          disabled={isLoading || shifts.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#C41E3A] text-white text-sm font-medium hover:bg-[#a91930] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      <p className="text-sm text-gray-500 max-w-3xl print:hidden">
        Every POS shift opened or closed at the till. Variances over the configured threshold required a manager PIN at close and a typed reason — both are shown here for reconciliation against the bank deposit. Read-only — shifts are opened + closed at the till.
      </p>

      {/* Filters bar */}
      <div className="rounded-lg border border-[#2e2e2e] bg-[#0f0f0f] p-3 flex flex-wrap items-end gap-3 text-sm print:hidden">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="bg-[#1a1a1a] border border-[#2e2e2e] text-gray-200 rounded px-2 py-1.5"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Variance</label>
          <select
            value={varianceFilter}
            onChange={(e) => setVarianceFilter(e.target.value as VarianceFilter)}
            className="bg-[#1a1a1a] border border-[#2e2e2e] text-gray-200 rounded px-2 py-1.5"
          >
            <option value="all">All</option>
            <option value="zero">Zero only</option>
            <option value="nonzero">Any non-zero</option>
            <option value="over_threshold">⚠ Over threshold</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="bg-[#1a1a1a] border border-[#2e2e2e] text-gray-200 rounded px-2 py-1.5"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="bg-[#1a1a1a] border border-[#2e2e2e] text-gray-200 rounded px-2 py-1.5"
          />
        </div>
        {(statusFilter !== 'all' || varianceFilter !== 'all' || fromDate || toDate) && (
          <button
            onClick={() => {
              setStatusFilter('all')
              setVarianceFilter('all')
              setFromDate('')
              setToDate('')
            }}
            className="text-xs text-gray-400 hover:text-white px-2 py-1.5 underline"
          >
            Clear filters
          </button>
        )}
        <div className="ml-auto text-xs text-gray-500">
          {isLoading ? '…' : `${shifts.length} shift${shifts.length === 1 ? '' : 's'}`}
        </div>
      </div>

      <div className="rounded-lg border border-[#2e2e2e] bg-[#0f0f0f] overflow-hidden print:hidden">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : shifts.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No shifts match the current filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-gray-500 bg-[#161616]">
              <tr>
                <th className="text-left px-4 py-3">Opened</th>
                <th className="text-left px-4 py-3">Closed</th>
                <th className="text-left px-4 py-3">Opened by</th>
                <th className="text-left px-4 py-3">Closed by</th>
                <th className="text-right px-4 py-3">Float</th>
                <th className="text-right px-4 py-3">Expected</th>
                <th className="text-right px-4 py-3">Counted</th>
                <th className="text-right px-4 py-3">Variance</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s) => {
                const v = s.variance_fils ?? 0
                return (
                  <tr
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className="border-t border-[#2e2e2e] hover:bg-[#161616] cursor-pointer"
                  >
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{formatDateTime(s.opened_at)}</td>
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{s.closed_at ? formatDateTime(s.closed_at) : '—'}</td>
                    <td className="px-4 py-3 text-gray-300">{s.opened_by_cashier_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-300">{s.closed_by_cashier_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-300 text-right whitespace-nowrap">{formatCurrency(s.starting_float_fils)}</td>
                    <td className="px-4 py-3 text-gray-300 text-right whitespace-nowrap">{s.expected_cash_fils !== null ? formatCurrency(s.expected_cash_fils) : '—'}</td>
                    <td className="px-4 py-3 text-gray-300 text-right whitespace-nowrap">{s.actual_cash_fils !== null ? formatCurrency(s.actual_cash_fils) : '—'}</td>
                    <td className={`px-4 py-3 text-right whitespace-nowrap ${v === 0 ? 'text-gray-300' : 'text-red-300'}`}>
                      {s.variance_fils === null ? '—' : (
                        <span className="inline-flex items-center gap-1">
                          {Math.abs(v) > 0 && <AlertCircle size={12} />}
                          {v >= 0 ? '+' : ''}{formatCurrency(v)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {s.status === 'open' ? (
                        <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-green-500/15 text-green-400">Open</span>
                      ) : (
                        <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-white/10 text-gray-300">Closed</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {selectedId && <ShiftDetailPanel shiftId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  )
}
